import { sessionStore } from 'utils/storage'

const DESKTOP_ANALYTICS_CONTEXT_KEY = 'cf-studio-desktop-context'

export type AppRuntime = 'web' | 'desktop'

/**
 * Context attached to every Amplitude event from the Studio web renderer.
 * Marketplace / Lumo / Freemode never register this, so those events stay untagged.
 */
export interface WebAnalyticsContext {
  app_runtime: 'web'
}

/**
 * Desktop shell context attached to every Amplitude event fired from the Studio
 * desktop renderer, so downstream metrics (trial starts, opens, …) can be split
 * by OS/app version without each caller passing it.
 */
export interface DesktopAnalyticsContext {
  app_runtime: 'desktop'
  desktop_platform: 'mac' | 'windows' | 'linux' | 'unknown'
  desktop_app_version: string | null
}

export type StudioAnalyticsContext = WebAnalyticsContext | DesktopAnalyticsContext

const WEB_ANALYTICS_CONTEXT: WebAnalyticsContext = { app_runtime: 'web' }

const DESKTOP_ANALYTICS_PREVIEW: DesktopAnalyticsContext = {
  app_runtime: 'desktop',
  desktop_platform: 'unknown',
  desktop_app_version: null,
}

/** Storage surface the context store needs; satisfied by `sessionStore`. */
type DesktopContextStore = Pick<typeof sessionStore, 'getJSON' | 'setJSON'>

/**
 * Builds a desktop-context store over an injectable persistence layer. The
 * store keeps an in-memory cache to avoid a storage read on every event, and
 * falls back to the injected store so context registered before a reload (or
 * before a queued-event drain) still applies within the session.
 *
 * The `store` seam exists for tests: pass a fake to verify persistence and the
 * reload fallback without touching real `sessionStorage` or resetting modules.
 */
export function createDesktopAnalyticsContextStore(store: DesktopContextStore = sessionStore) {
  let cachedContext: StudioAnalyticsContext | null = null

  const api = {
    set(context: StudioAnalyticsContext) {
      cachedContext = context
      store.setJSON(DESKTOP_ANALYTICS_CONTEXT_KEY, context)
    },
    get(): StudioAnalyticsContext | null {
      if (!cachedContext) {
        cachedContext = store.getJSON<StudioAnalyticsContext>(DESKTOP_ANALYTICS_CONTEXT_KEY)
      }
      return cachedContext
    },
    /**
     * Boot-time registration: stamp `app_runtime` without wiping a fuller
     * desktop payload already persisted from a previous load in this session.
     */
    ensureAppRuntime(kind: AppRuntime) {
      const existing = api.get()
      if (kind === 'desktop') {
        if (existing?.app_runtime === 'desktop') {
          return
        }
        api.set(DESKTOP_ANALYTICS_PREVIEW)
        return
      }
      if (existing?.app_runtime === 'web') {
        return
      }
      api.set(WEB_ANALYTICS_CONTEXT)
    },
  }

  return api
}

const desktopAnalyticsContextStore = createDesktopAnalyticsContextStore()

/**
 * Registers analytics context. Persisted via the store so events fired before
 * the registering hook re-runs (after a reload, or from the queued-event drain)
 * still carry it within the session.
 */
export const setDesktopAnalyticsContext = (context: StudioAnalyticsContext) => {
  desktopAnalyticsContextStore.set(context)
}

export const getDesktopAnalyticsContext = (): StudioAnalyticsContext | null => {
  return desktopAnalyticsContextStore.get()
}

export const setStudioWebAnalyticsContext = () => {
  desktopAnalyticsContextStore.set(WEB_ANALYTICS_CONTEXT)
}

/**
 * Studio boot helper. Call once from the Studio entry with the synchronous
 * desktop-bridge check so early events (e.g. Loaded Studio) are tagged before
 * capabilities resolve.
 */
export const ensureStudioAppRuntime = (kind: AppRuntime) => {
  desktopAnalyticsContextStore.ensureAppRuntime(kind)
}
